// theme-engine.ts — Sprint 019 + 020: Executive Theme Engine™ + Typography Engine™
//
// ARCHITECTURE:
//   Theme Engine is a pure presentation concern.
//   Mission Director, Execution Engine, Outcome Engine, Operations Engine
//   remain completely unaware of themes or typography.
//
// RULES:
//   • No component knows specific colors or font families — all consume CSS variables.
//   • Color themes and typography are independent systems — mix and match freely.
//   • Token values are the single source of truth for all visual styling.
//   • No hardcoded font families in components.

// ─── Color Theme ID ───────────────────────────────────────────────────────────

export type ThemeId =
  | 'executive-light'
  | 'midnight-ops'
  | 'arctic-blue'
  | 'executive-purple'
  | 'oled-black'
  | 'matrix-classic';

// ─── Font Style ID ────────────────────────────────────────────────────────────

export type FontStyleId =
  | 'default'       // Resolves to the active theme's embedded typography
  | 'executive'     // Space Grotesk · Inter · JetBrains Mono — premium exec
  | 'modern'        // Manrope · Manrope · IBM Plex Mono — geometric clean
  | 'minimal'       // Inter · Inter · Roboto Mono — max readability
  | 'technical'     // IBM Plex Mono all — engineering terminal
  | 'retro'         // Share Tech Mono · IBM Plex Mono — classic terminal
  | 'luxury';       // Sora · Sora · Space Mono — elegant premium

// ─── Appearance Config Types ──────────────────────────────────────────────────

export type FontSize       = 'small' | 'default' | 'large';
export type UIDensity      = 'compact' | 'default' | 'comfortable';
export type CornerStyle    = 'sharp' | 'rounded' | 'pill';
export type AnimationLevel = 'none' | 'minimal' | 'standard' | 'full';

// ─── Appearance Config (persisted to localStorage) ────────────────────────────

export interface AppearanceConfig {
  themeId:        ThemeId;
  fontStyleId:    FontStyleId;
  fontSize:       FontSize;
  density:        UIDensity;
  cornerStyle:    CornerStyle;
  animationLevel: AnimationLevel;
}

export const DEFAULT_APPEARANCE: AppearanceConfig = {
  themeId:        'executive-light',
  fontStyleId:    'default',
  fontSize:       'default',
  density:        'default',
  cornerStyle:    'sharp',
  animationLevel: 'standard',
};

// ─── Color Tokens ─────────────────────────────────────────────────────────────
// Each value is a raw "H S% L%" string used inside hsl(var(--token)).

export interface ThemeTokens {
  background:          string;
  foreground:          string;
  card:                string;
  cardForeground:      string;
  cardBorder:          string;
  border:              string;
  input:               string;
  ring:                string;
  primary:             string;
  primaryForeground:   string;
  secondary:           string;
  secondaryForeground: string;
  muted:               string;
  mutedForeground:     string;
  destructive:         string;
  destructiveForeground: string;
}

// ─── Typography Tokens ────────────────────────────────────────────────────────
// Font values are full CSS font-family strings, e.g. "'Inter', sans-serif".
// These override --font-sans and --font-mono (Tailwind utilities) plus
// add new CSS vars --font-display, --font-heading, etc.

export interface TypographyTokens {
  fontDisplay:   string;  // Major hero/display headings
  fontHeading:   string;  // Section headings
  fontBody:      string;  // Body text — overrides --font-sans
  fontMono:      string;  // Monospace — overrides --font-mono (affects ALL font-mono classes)
  fontButton:    string;  // Button labels
  fontStatus:    string;  // Status badges and indicators
  fontNumbers:   string;  // Numeric metrics and values
  letterSpacing: string;  // CSS letter-spacing for headings
  lineHeight:    string;  // CSS line-height for body
  fontWeight:    string;  // Heading font-weight
}

// ─── CSS Variable Maps ────────────────────────────────────────────────────────

export const TOKEN_TO_CSS_VAR: Record<keyof ThemeTokens, string> = {
  background:          '--background',
  foreground:          '--foreground',
  card:                '--card',
  cardForeground:      '--card-foreground',
  cardBorder:          '--card-border',
  border:              '--border',
  input:               '--input',
  ring:                '--ring',
  primary:             '--primary',
  primaryForeground:   '--primary-foreground',
  secondary:           '--secondary',
  secondaryForeground: '--secondary-foreground',
  muted:               '--muted',
  mutedForeground:     '--muted-foreground',
  destructive:         '--destructive',
  destructiveForeground: '--destructive-foreground',
};

// Typography CSS var map.
// fontBody → --font-sans (overrides Tailwind font-sans for all components)
// fontMono → --font-mono (overrides Tailwind font-mono for ALL font-mono components)
export const TYPOGRAPHY_CSS_VARS: Record<keyof TypographyTokens, string> = {
  fontDisplay:   '--font-display',
  fontHeading:   '--font-heading',
  fontBody:      '--font-sans',      // ← overrides Tailwind --font-sans
  fontMono:      '--font-mono',      // ← overrides Tailwind --font-mono
  fontButton:    '--font-button',
  fontStatus:    '--font-status',
  fontNumbers:   '--font-numbers',
  letterSpacing: '--letter-spacing-ui',
  lineHeight:    '--line-height-ui',
  fontWeight:    '--font-weight-heading',
};

// ─── Corner Radius Map ────────────────────────────────────────────────────────

export const CORNER_RADIUS_MAP: Record<CornerStyle, { sm: string; md: string; lg: string; xl: string }> = {
  sharp:   { sm: '0px',     md: '0px',     lg: '0px',     xl: '0px' },
  rounded: { sm: '4px',     md: '6px',     lg: '8px',     xl: '12px' },
  pill:    { sm: '9999px',  md: '9999px',  lg: '9999px',  xl: '9999px' },
};

// ─── Font Size Map ────────────────────────────────────────────────────────────

export const FONT_SIZE_MAP: Record<FontSize, string> = {
  small:   '13px',
  default: '15px',
  large:   '17px',
};

// ─── Animation Config ─────────────────────────────────────────────────────────

export type AnimationIntensity = 'none' | 'minimal' | 'moderate' | 'full';

export interface AnimationConfig {
  intensity:   AnimationIntensity;
  scanLine:    boolean;
  statusPulse: boolean;
  heroGlow:    boolean;
  borderGlow:  boolean;
  cmdGrid:     boolean;
}

// ─── Theme Definition ─────────────────────────────────────────────────────────

export interface ThemeDefinition {
  id:              ThemeId;
  name:            string;
  tagline:         string;
  isDark:          boolean;
  tokens:          ThemeTokens;
  typography:      TypographyTokens;   // Default typography for this theme
  defaultFontStyle: FontStyleId;       // Which font style preset is the default
  animation:       AnimationConfig;
  // Settings picker preview
  previewBg:       string;
  previewCard:     string;
  previewAccent:   string;
  previewText:     string;
  previewBorder:   string;
  previewSwatches: string[];
}

// ─── Font Style Definition ────────────────────────────────────────────────────

export interface FontStyleDefinition {
  id:          FontStyleId;
  name:        string;
  tagline:     string;
  typography:  TypographyTokens;
  // Fonts to load from Google Fonts
  googleFonts: string[];
}

// ─── Per-theme Typography (embedded defaults) ─────────────────────────────────

const TYPOGRAPHY_EXECUTIVE_LIGHT: TypographyTokens = {
  fontDisplay:   "'Space Grotesk', sans-serif",
  fontHeading:   "'Inter', sans-serif",
  fontBody:      "'Inter', sans-serif",
  fontMono:      "'JetBrains Mono', monospace",
  fontButton:    "'Inter', sans-serif",
  fontStatus:    "'JetBrains Mono', monospace",
  fontNumbers:   "'IBM Plex Mono', monospace",
  letterSpacing: '-0.01em',
  lineHeight:    '1.6',
  fontWeight:    '600',
};

const TYPOGRAPHY_MIDNIGHT_OPS: TypographyTokens = {
  fontDisplay:   "'Inter', sans-serif",
  fontHeading:   "'Inter', sans-serif",
  fontBody:      "'Inter', sans-serif",
  fontMono:      "'JetBrains Mono', monospace",
  fontButton:    "'Inter', sans-serif",
  fontStatus:    "'JetBrains Mono', monospace",
  fontNumbers:   "'Roboto Mono', monospace",
  letterSpacing: '0em',
  lineHeight:    '1.5',
  fontWeight:    '600',
};

const TYPOGRAPHY_ARCTIC_BLUE: TypographyTokens = {
  fontDisplay:   "'Manrope', sans-serif",
  fontHeading:   "'Manrope', sans-serif",
  fontBody:      "'Manrope', sans-serif",
  fontMono:      "'IBM Plex Mono', monospace",
  fontButton:    "'Manrope', sans-serif",
  fontStatus:    "'IBM Plex Mono', monospace",
  fontNumbers:   "'IBM Plex Mono', monospace",
  letterSpacing: '0em',
  lineHeight:    '1.6',
  fontWeight:    '600',
};

const TYPOGRAPHY_EXECUTIVE_PURPLE: TypographyTokens = {
  fontDisplay:   "'Sora', sans-serif",
  fontHeading:   "'Sora', sans-serif",
  fontBody:      "'Inter', sans-serif",
  fontMono:      "'Space Mono', monospace",
  fontButton:    "'Sora', sans-serif",
  fontStatus:    "'Space Mono', monospace",
  fontNumbers:   "'Space Mono', monospace",
  letterSpacing: '-0.02em',
  lineHeight:    '1.55',
  fontWeight:    '700',
};

const TYPOGRAPHY_OLED_BLACK: TypographyTokens = {
  fontDisplay:   "'Outfit', sans-serif",
  fontHeading:   "'Outfit', sans-serif",
  fontBody:      "'Inter', sans-serif",
  fontMono:      "'JetBrains Mono', monospace",
  fontButton:    "'Outfit', sans-serif",
  fontStatus:    "'JetBrains Mono', monospace",
  fontNumbers:   "'Roboto Mono', monospace",
  letterSpacing: '0em',
  lineHeight:    '1.5',
  fontWeight:    '600',
};

const TYPOGRAPHY_MATRIX_CLASSIC: TypographyTokens = {
  fontDisplay:   "'Share Tech Mono', monospace",
  fontHeading:   "'IBM Plex Mono', monospace",
  fontBody:      "'IBM Plex Mono', monospace",
  fontMono:      "'Share Tech Mono', monospace",
  fontButton:    "'Share Tech Mono', monospace",
  fontStatus:    "'Share Tech Mono', monospace",
  fontNumbers:   "'Share Tech Mono', monospace",
  letterSpacing: '0.08em',
  lineHeight:    '1.5',
  fontWeight:    '400',
};

// Record mapping ThemeId → default typography
export const THEME_TYPOGRAPHY: Record<ThemeId, TypographyTokens> = {
  'executive-light':   TYPOGRAPHY_EXECUTIVE_LIGHT,
  'midnight-ops':      TYPOGRAPHY_MIDNIGHT_OPS,
  'arctic-blue':       TYPOGRAPHY_ARCTIC_BLUE,
  'executive-purple':  TYPOGRAPHY_EXECUTIVE_PURPLE,
  'oled-black':        TYPOGRAPHY_OLED_BLACK,
  'matrix-classic':    TYPOGRAPHY_MATRIX_CLASSIC,
};

// ─── Font Style Presets ───────────────────────────────────────────────────────
// Independent from color themes. User can mix any color theme + any font style.

export const FONT_STYLES: Record<Exclude<FontStyleId, 'default'>, FontStyleDefinition> = {

  executive: {
    id: 'executive',
    name: 'Executive',
    tagline: 'Space Grotesk · Inter · JetBrains Mono',
    typography: {
      fontDisplay:   "'Space Grotesk', sans-serif",
      fontHeading:   "'Inter', sans-serif",
      fontBody:      "'Inter', sans-serif",
      fontMono:      "'JetBrains Mono', monospace",
      fontButton:    "'Inter', sans-serif",
      fontStatus:    "'JetBrains Mono', monospace",
      fontNumbers:   "'IBM Plex Mono', monospace",
      letterSpacing: '-0.01em',
      lineHeight:    '1.6',
      fontWeight:    '600',
    },
    googleFonts: ['Space Grotesk', 'Inter', 'JetBrains Mono', 'IBM Plex Mono'],
  },

  modern: {
    id: 'modern',
    name: 'Modern',
    tagline: 'Manrope · Manrope · IBM Plex Mono',
    typography: {
      fontDisplay:   "'Manrope', sans-serif",
      fontHeading:   "'Manrope', sans-serif",
      fontBody:      "'Manrope', sans-serif",
      fontMono:      "'IBM Plex Mono', monospace",
      fontButton:    "'Manrope', sans-serif",
      fontStatus:    "'IBM Plex Mono', monospace",
      fontNumbers:   "'IBM Plex Mono', monospace",
      letterSpacing: '0em',
      lineHeight:    '1.6',
      fontWeight:    '600',
    },
    googleFonts: ['Manrope', 'IBM Plex Mono'],
  },

  minimal: {
    id: 'minimal',
    name: 'Minimal',
    tagline: 'Inter · Inter · Roboto Mono',
    typography: {
      fontDisplay:   "'Inter', sans-serif",
      fontHeading:   "'Inter', sans-serif",
      fontBody:      "'Inter', sans-serif",
      fontMono:      "'Roboto Mono', monospace",
      fontButton:    "'Inter', sans-serif",
      fontStatus:    "'Roboto Mono', monospace",
      fontNumbers:   "'Roboto Mono', monospace",
      letterSpacing: '0em',
      lineHeight:    '1.65',
      fontWeight:    '500',
    },
    googleFonts: ['Inter', 'Roboto Mono'],
  },

  technical: {
    id: 'technical',
    name: 'Technical',
    tagline: 'IBM Plex Mono · IBM Plex Mono · IBM Plex Mono',
    typography: {
      fontDisplay:   "'IBM Plex Mono', monospace",
      fontHeading:   "'IBM Plex Mono', monospace",
      fontBody:      "'IBM Plex Mono', monospace",
      fontMono:      "'IBM Plex Mono', monospace",
      fontButton:    "'IBM Plex Mono', monospace",
      fontStatus:    "'IBM Plex Mono', monospace",
      fontNumbers:   "'IBM Plex Mono', monospace",
      letterSpacing: '0.04em',
      lineHeight:    '1.55',
      fontWeight:    '500',
    },
    googleFonts: ['IBM Plex Mono'],
  },

  retro: {
    id: 'retro',
    name: 'Retro',
    tagline: 'Share Tech Mono · IBM Plex Mono',
    typography: {
      fontDisplay:   "'Share Tech Mono', monospace",
      fontHeading:   "'IBM Plex Mono', monospace",
      fontBody:      "'IBM Plex Mono', monospace",
      fontMono:      "'Share Tech Mono', monospace",
      fontButton:    "'Share Tech Mono', monospace",
      fontStatus:    "'Share Tech Mono', monospace",
      fontNumbers:   "'Share Tech Mono', monospace",
      letterSpacing: '0.1em',
      lineHeight:    '1.5',
      fontWeight:    '400',
    },
    googleFonts: ['Share Tech Mono', 'IBM Plex Mono'],
  },

  luxury: {
    id: 'luxury',
    name: 'Luxury',
    tagline: 'Sora · Sora · Space Mono',
    typography: {
      fontDisplay:   "'Sora', sans-serif",
      fontHeading:   "'Sora', sans-serif",
      fontBody:      "'Sora', sans-serif",
      fontMono:      "'Space Mono', monospace",
      fontButton:    "'Sora', sans-serif",
      fontStatus:    "'Space Mono', monospace",
      fontNumbers:   "'Space Mono', monospace",
      letterSpacing: '-0.02em',
      lineHeight:    '1.6',
      fontWeight:    '700',
    },
    googleFonts: ['Sora', 'Space Mono'],
  },
};

// ─── Theme Definitions ────────────────────────────────────────────────────────
// Sprint 026 — Theme System Refinement™
// All tokens: same hue + saturation as before. Only lightness adjusted for
// contrast, border visibility, and muted text readability. Identity preserved.

export const THEMES: Record<ThemeId, ThemeDefinition> = {

  'executive-light': {
    id: 'executive-light',
    name: 'Executive Light™',
    tagline: 'Clean. Professional. Premium.',
    isDark: false,
    tokens: {
      background:          '0 0% 97%',
      foreground:          '220 13% 11%',     // was 12% — marginally stronger
      card:                '0 0% 100%',
      cardForeground:      '220 13% 11%',
      cardBorder:          '220 13% 80%',     // was 88% — borders now clearly visible on white cards
      border:              '220 13% 80%',     // was 88% — clearly visible on 97% bg
      input:               '220 13% 90%',     // was 93% — more distinct from card
      ring:                '217 91% 60%',
      primary:             '217 91% 60%',
      primaryForeground:   '0 0% 100%',
      secondary:           '220 13% 92%',     // was 93%
      secondaryForeground: '220 13% 22%',     // was 28% — stronger on light bg
      muted:               '220 13% 95%',
      mutedForeground:     '220 13% 38%',     // was 45% — comfortably readable on light bg
      destructive:         '0 84% 60%',
      destructiveForeground: '0 0% 100%',
    },
    typography:       TYPOGRAPHY_EXECUTIVE_LIGHT,
    defaultFontStyle: 'executive',
    animation: {
      intensity: 'minimal',
      scanLine: false, statusPulse: false,
      heroGlow: false, borderGlow: false, cmdGrid: false,
    },
    previewBg:       'hsl(0 0% 97%)',
    previewCard:     'hsl(0 0% 100%)',
    previewAccent:   'hsl(217 91% 60%)',
    previewText:     'hsl(220 13% 11%)',
    previewBorder:   'hsl(220 13% 80%)',
    previewSwatches: ['hsl(0 0% 97%)', 'hsl(0 0% 100%)', 'hsl(217 91% 60%)', 'hsl(220 13% 11%)'],
  },

  'midnight-ops': {
    id: 'midnight-ops',
    name: 'Midnight Operations™',
    tagline: 'Professional dark. Low eye strain.',
    isDark: true,
    tokens: {
      background:          '222 14% 10%',
      foreground:          '220 10% 88%',     // was 84% — brighter for comfortable reading
      card:                '222 14% 13%',
      cardForeground:      '220 10% 88%',
      cardBorder:          '222 14% 28%',     // was 20% — cards now feel like distinct modules
      border:              '222 14% 26%',     // was 19% — clearly visible on dark bg
      input:               '222 14% 20%',     // was 18%
      ring:                '213 76% 58%',
      primary:             '213 76% 58%',
      primaryForeground:   '0 0% 100%',
      secondary:           '222 14% 20%',     // was 18%
      secondaryForeground: '220 10% 72%',     // was 68% — more readable
      muted:               '222 14% 16%',
      mutedForeground:     '220 10% 62%',     // was 54% — no squinting required
      destructive:         '0 72% 58%',
      destructiveForeground: '0 0% 100%',
    },
    typography:       TYPOGRAPHY_MIDNIGHT_OPS,
    defaultFontStyle: 'executive',
    animation: {
      intensity: 'minimal',
      scanLine: false, statusPulse: true,
      heroGlow: false, borderGlow: false, cmdGrid: false,
    },
    previewBg:       'hsl(222 14% 10%)',
    previewCard:     'hsl(222 14% 13%)',
    previewAccent:   'hsl(213 76% 58%)',
    previewText:     'hsl(220 10% 88%)',
    previewBorder:   'hsl(222 14% 26%)',
    previewSwatches: ['hsl(222 14% 10%)', 'hsl(222 14% 13%)', 'hsl(213 76% 58%)', 'hsl(220 10% 88%)'],
  },

  'arctic-blue': {
    id: 'arctic-blue',
    name: 'Arctic Blue™',
    tagline: 'Cool clarity. Ice-blue precision.',
    isDark: false,
    tokens: {
      background:          '210 40% 97%',
      foreground:          '210 40% 8%',      // was 9% — marginally stronger
      card:                '0 0% 100%',
      cardForeground:      '210 40% 8%',
      cardBorder:          '210 40% 76%',     // was 86% — dramatically more visible on white cards
      border:              '210 40% 76%',     // was 87% — clearly visible on blue-tinted bg
      input:               '210 40% 90%',     // was 93%
      ring:                '197 100% 42%',
      primary:             '197 100% 42%',
      primaryForeground:   '0 0% 100%',
      secondary:           '210 40% 91%',     // was 92%
      secondaryForeground: '210 40% 18%',     // was 22% — stronger on light bg
      muted:               '210 40% 94%',     // was 95%
      mutedForeground:     '210 40% 35%',     // was 42% — comfortably readable on light bg
      destructive:         '0 84% 60%',
      destructiveForeground: '0 0% 100%',
    },
    typography:       TYPOGRAPHY_ARCTIC_BLUE,
    defaultFontStyle: 'modern',
    animation: {
      intensity: 'minimal',
      scanLine: false, statusPulse: false,
      heroGlow: false, borderGlow: false, cmdGrid: false,
    },
    previewBg:       'hsl(210 40% 97%)',
    previewCard:     'hsl(0 0% 100%)',
    previewAccent:   'hsl(197 100% 42%)',
    previewText:     'hsl(210 40% 8%)',
    previewBorder:   'hsl(210 40% 76%)',
    previewSwatches: ['hsl(210 40% 97%)', 'hsl(0 0% 100%)', 'hsl(197 100% 42%)', 'hsl(210 40% 8%)'],
  },

  'executive-purple': {
    id: 'executive-purple',
    name: 'Executive Purple™',
    tagline: 'Luxury authority. Deep precision.',
    isDark: true,
    tokens: {
      background:          '260 20% 7%',
      foreground:          '260 8% 92%',      // was 88% — stronger presence
      card:                '260 20% 12%',     // was 10% — more separation from bg
      cardForeground:      '260 8% 92%',
      cardBorder:          '260 20% 28%',     // was 19% — operational module definition
      border:              '260 20% 25%',     // was 17% — confident, not disappearing
      input:               '260 20% 20%',     // was 17%
      ring:                '262 72% 64%',
      primary:             '262 72% 64%',
      primaryForeground:   '0 0% 100%',
      secondary:           '260 20% 20%',     // was 17%
      secondaryForeground: '260 8% 74%',      // was 70% — more readable
      muted:               '260 20% 16%',     // was 14%
      mutedForeground:     '260 8% 60%',      // was 50% — never struggle to read
      destructive:         '0 72% 58%',
      destructiveForeground: '0 0% 100%',
    },
    typography:       TYPOGRAPHY_EXECUTIVE_PURPLE,
    defaultFontStyle: 'luxury',
    animation: {
      intensity: 'moderate',
      scanLine: false, statusPulse: true,
      heroGlow: true, borderGlow: false, cmdGrid: false,
    },
    previewBg:       'hsl(260 20% 7%)',
    previewCard:     'hsl(260 20% 12%)',
    previewAccent:   'hsl(262 72% 64%)',
    previewText:     'hsl(260 8% 92%)',
    previewBorder:   'hsl(260 20% 25%)',
    previewSwatches: ['hsl(260 20% 7%)', 'hsl(260 20% 12%)', 'hsl(262 72% 64%)', 'hsl(260 8% 92%)'],
  },

  'oled-black': {
    id: 'oled-black',
    name: 'OLED Black™',
    tagline: 'True black. Maximum contrast.',
    isDark: true,
    tokens: {
      background:          '0 0% 0%',
      foreground:          '0 0% 95%',        // was 94% — crisp white on black
      card:                '0 0% 4%',
      cardForeground:      '0 0% 95%',
      cardBorder:          '0 0% 18%',        // was 12% — cards feel intentional
      border:              '0 0% 17%',        // was 11% — confident module borders
      input:               '0 0% 13%',        // was 11%
      ring:                '0 0% 88%',
      primary:             '0 0% 88%',
      primaryForeground:   '0 0% 0%',
      secondary:           '0 0% 9%',         // was 8%
      secondaryForeground: '0 0% 72%',        // was 68% — more readable
      muted:               '0 0% 7%',         // was 6%
      mutedForeground:     '0 0% 56%',        // was 48% — comfortably readable
      destructive:         '0 84% 60%',
      destructiveForeground: '0 0% 100%',
    },
    typography:       TYPOGRAPHY_OLED_BLACK,
    defaultFontStyle: 'minimal',
    animation: {
      intensity: 'none',
      scanLine: false, statusPulse: false,
      heroGlow: false, borderGlow: false, cmdGrid: false,
    },
    previewBg:       'hsl(0 0% 0%)',
    previewCard:     'hsl(0 0% 4%)',
    previewAccent:   'hsl(0 0% 88%)',
    previewText:     'hsl(0 0% 95%)',
    previewBorder:   'hsl(0 0% 17%)',
    previewSwatches: ['hsl(0 0% 0%)', 'hsl(0 0% 4%)', 'hsl(0 0% 88%)', 'hsl(0 0% 95%)'],
  },

  'matrix-classic': {
    id: 'matrix-classic',
    name: 'Matrix Classic™',
    tagline: 'The original. Neon green terminal.',
    isDark: true,
    tokens: {
      background:          '0 0% 4%',
      foreground:          '0 0% 93%',        // was 90% — stronger text presence
      card:                '0 0% 9%',         // was 6% — cards clearly separated from bg
      cardForeground:      '0 0% 93%',
      cardBorder:          '0 0% 22%',        // was 15% — modules feel intentional
      border:              '0 0% 22%',        // was 15% — confident borders
      input:               '0 0% 18%',        // was 15%
      ring:                '135 100% 50%',
      primary:             '135 100% 50%',
      primaryForeground:   '0 0% 0%',
      secondary:           '0 0% 15%',        // was 12%
      secondaryForeground: '0 0% 85%',        // was 80% — more readable
      muted:               '0 0% 12%',        // was 10%
      mutedForeground:     '0 0% 68%',        // was 60% — no squinting on terminal text
      destructive:         '0 100% 50%',
      destructiveForeground: '0 0% 100%',
    },
    typography:       TYPOGRAPHY_MATRIX_CLASSIC,
    defaultFontStyle: 'retro',
    animation: {
      intensity: 'full',
      scanLine: true, statusPulse: true,
      heroGlow: true, borderGlow: true, cmdGrid: true,
    },
    previewBg:       'hsl(0 0% 4%)',
    previewCard:     'hsl(0 0% 9%)',
    previewAccent:   'hsl(135 100% 50%)',
    previewText:     'hsl(0 0% 93%)',
    previewBorder:   'hsl(0 0% 22%)',
    previewSwatches: ['hsl(0 0% 4%)', 'hsl(0 0% 9%)', 'hsl(135 100% 50%)', 'hsl(0 0% 93%)'],
  },
};

// ─── Convenience exports ──────────────────────────────────────────────────────

export const DEFAULT_THEME_ID: ThemeId = 'executive-light';

export const THEME_LIST: ThemeDefinition[] = [
  THEMES['executive-light'],
  THEMES['midnight-ops'],
  THEMES['arctic-blue'],
  THEMES['executive-purple'],
  THEMES['oled-black'],
  THEMES['matrix-classic'],
];

export const FONT_STYLE_LIST: Array<{ id: FontStyleId; name: string; tagline: string }> = [
  { id: 'default',    name: 'Default',   tagline: 'Matches selected theme' },
  { id: 'executive',  name: 'Executive', tagline: 'Space Grotesk · Inter · JetBrains Mono' },
  { id: 'modern',     name: 'Modern',    tagline: 'Manrope · IBM Plex Mono' },
  { id: 'minimal',    name: 'Minimal',   tagline: 'Inter · Roboto Mono' },
  { id: 'technical',  name: 'Technical', tagline: 'IBM Plex Mono · all mono' },
  { id: 'retro',      name: 'Retro',     tagline: 'Share Tech Mono · IBM Plex Mono' },
  { id: 'luxury',     name: 'Luxury',    tagline: 'Sora · Space Mono' },
];

// ─── Typography resolver ──────────────────────────────────────────────────────
// Resolves the active typography tokens given current themeId + fontStyleId.

export function resolveTypography(themeId: ThemeId, fontStyleId: FontStyleId): TypographyTokens {
  if (fontStyleId === 'default') return THEME_TYPOGRAPHY[themeId];
  return FONT_STYLES[fontStyleId].typography;
}

// ─── Google Fonts extractor ───────────────────────────────────────────────────
// Extracts font family names from CSS font-family strings.

export function extractFontNames(typography: TypographyTokens): string[] {
  const seen = new Set<string>();
  const fields: (keyof TypographyTokens)[] = [
    'fontDisplay', 'fontHeading', 'fontBody', 'fontMono',
    'fontButton', 'fontStatus', 'fontNumbers',
  ];
  for (const field of fields) {
    const value = typography[field];
    const match = value.match(/'([^']+)'/);
    if (match) seen.add(match[1]);
  }
  return Array.from(seen);
}
